<template>
  <v-app>
    <v-main>
      <form>
        <v-container grid-list-md class="ghost">
          <v-row class="justify-space-between">
            <v-col cols="10" sm="3">
              <v-text-field
                label="Patient ID / Name"
                outlined
                class="iol-v-text-field"
                ref="first"
                v-model="inputs.patientID"
              />
            </v-col>
            <v-col cols="10" sm="3">
              <v-select
                label="Keratometric Index"
                outlined
                class="smooth iol-v-select"
                v-model="inputs.keratometric_index"
                :items="$parent.keratometricItems"
                return-object
                required
              />
            </v-col>
            <v-col cols="10" sm="3">
              <v-select
                label="Biometer"
                :items="$parent.biometerItems"
                item-text="text"
                outlined
                class="smooth iol-v-select"
                v-model="inputs.biometer"
                return-object
                required
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <br />
            </v-col>
          </v-row>
          <v-sheet
            :class="inputs.side ? 'iol-right-v-sheet' : 'iol-left-v-sheet'"
          >
            <v-row class="align-baseline">
              <v-col cols="12">
                <v-btn
                  v-if="$vuetify.breakpoint.mobile"
                  @click="goToCamera"
                  color="#3B4256"
                  class="white--text"
                  style="width: 100%;"
                  x-large
                >
                  <v-icon class="mr-2">mdi-camera</v-icon>
                  Auto fill
                </v-btn>
              </v-col>
              <v-col cols="2" md="5" class="switch-eye-label-left">
                <transition name="fade">
                  <div v-if="inputs.side">
                    <h1>OD</h1>
                  </div>
                </transition>
              </v-col>
              <v-spacer />
              <v-col cols="2" md="1">
                <v-switch
                  v-model="inputs.side"
                  class="switch-eye"
                  :messages="
                    inputs.side ? 'switch to left eye' : 'switch to right eye'
                  "
                />
              </v-col>
              <v-spacer />
              <v-col cols="2" md="5" class="switch-eye-label-right">
                <transition name="fade">
                  <div v-if="!inputs.side">
                    <h1>OS</h1>
                  </div>
                </transition>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-sheet class="iol-v-sheet">
                  <v-row>
                    <v-col cols="6" md="3">
                      <v-select
                        label="IOL Model"
                        :items="$parent.IOLModelItems"
                        item-text="text"
                        outlined
                        v-model="inputs.iol_model"
                        return-object
                        required
                        class="smooth iol-v-select"
                        @change="updateAConst(inputs)"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="6" md="3">
                      <v-text-field
                        label="A constant"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.aconst"
                        :error-messages="getErrors('aconst', $v.inputs.aconst)"
                        :disabled="inputs.iol_model.type == '60MA'"
                        :required="inputs.iol_model.type != '60MA'"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.aconst.$touch()"
                        @blur="$v.inputs.aconst.$touch()"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="12" md="4">
                      <v-text-field
                        label="Target refraction (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.target"
                        :error-messages="getErrors('target', $v.inputs.target)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.target.$touch()"
                        @blur="$v.inputs.target.$touch()"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" sm="5">
                      <v-text-field
                        label="AL (mm)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.al"
                        :error-messages="getErrors('al', $v.inputs.al)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.al.$touch()"
                        @blur="$v.inputs.al.$touch()"
                      />
                      <v-text-field
                        label="K1 (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.k1"
                        :error-messages="getErrors('k1', $v.inputs.k1)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k1.$touch()"
                        @blur="$v.inputs.k1.$touch()"
                      />
                      <v-text-field
                        label="K2 (D)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.k2"
                        :error-messages="getErrors('k2', $v.inputs.k2)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k2.$touch()"
                        @blur="$v.inputs.k2.$touch()"
                      />
                      <v-text-field
                        label="ACD (mm)"
                        outlined
                        class="iol-v-text-field"
                        v-model="inputs.acd"
                        :error-messages="getErrors('acd', $v.inputs.acd)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.acd.$touch()"
                        @blur="$v.inputs.acd.$touch()"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="6" sm="6">
                      <v-text-field
                        label="Custom vitreous"
                        outlined
                        class="iol-v-text-field"
                        value="1.336"
                        readonly
                        disabled
                      />
                      <v-sheet class="iol-opt-v-sheet">
                        <h3>Optional</h3>
                        <br />
                        <v-text-field
                          label="LT (mm)"
                          outlined
                          class="iol-v-text-field"
                          v-model="inputs.lt"
                          :error-messages="getErrors('lt', $v.inputs.lt)"
                          type="number"
                          step="0.01"
                          @input="$v.inputs.lt.$touch()"
                          @blur="$v.inputs.lt.$touch()"
                        />
                        <v-text-field
                          label="CCT (µm)"
                          outlined
                          class="iol-v-text-field"
                          v-model="inputs.cct"
                          :error-messages="getErrors('cct', $v.inputs.cct)"
                          type="number"
                          step="0"
                          @input="$v.inputs.cct.$touch()"
                          @blur="$v.inputs.cct.$touch()"
                        />
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="6">
                <v-sheet class="iol-v-sheet">
                  <v-row>
                    <v-col cols="12" sm="12">
                      <v-sheet class="iol-opt-v-sheet">
                        <v-row>
                          <v-col cols="8" md="8">
                            <h3>Optional: Laser Vision Correction History ?</h3>
                          </v-col>
                          <v-col cols="4" md="4">
                            <v-switch
                              class="ma-0 pa-0"
                              hide-details
                              v-model="inputs.is_corneal_laser"
                            >
                            </v-switch>
                          </v-col>
                        </v-row>
                        <br />
                        <v-row class="justify-left">
                          <v-col cols="8" md="6" offset="1">
                            <v-select
                              label="Corneal refractive surgery"
                              :items="$parent.cornealLaserTypeItems"
                              item-text="text"
                              outlined
                              class="smooth iol-v-select"
                              v-model="inputs.corneal_laser_type"
                              return-object
                              :disabled="!inputs.is_corneal_laser"
                              :required="inputs.is_corneal_laser"
                            />
                          </v-col>
                        </v-row>
                        <v-row class="justify-center">
                          <v-col cols="8" md="8">
                            <v-tooltip bottom slot="append">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  label="Pre-LVC anterior corneal radius (mm)"
                                  outlined
                                  class="iol-v-text-field"
                                  v-model="inputs.pre_lvc_arc"
                                  :error-messages="
                                    getErrors(
                                      'pre_lvc_arc',
                                      $v.inputs.pre_lvc_arc,
                                    )
                                  "
                                  :disabled="!inputs.is_corneal_laser"
                                  :required="inputs.is_corneal_laser"
                                  type="number"
                                  step="0.01"
                                  @change="$v.inputs.pre_lvc_arc.$touch()"
                                  @blur="$v.inputs.pre_lvc_arc.$touch()"
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <span
                                >If available, enter the anterior corneal radius
                                measured before laser vision correction, in
                                millimeters</span
                              >
                            </v-tooltip>
                          </v-col>
                        </v-row>
                        <v-row class="justify-center">
                          <v-col cols="8" md="8">
                            <v-tooltip bottom slot="append">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  label="Corrected ametropia (D)"
                                  outlined
                                  class="iol-v-text-field"
                                  v-model="inputs.laser_amount"
                                  :error-messages="
                                    getErrors(
                                      'laser_amount',
                                      $v.inputs.laser_amount,
                                    )
                                  "
                                  :disabled="!inputs.is_corneal_laser"
                                  :required="inputs.is_corneal_laser"
                                  type="number"
                                  step="0.01"
                                  @change="$v.inputs.laser_amount.$touch()"
                                  @blur="$v.inputs.laser_amount.$touch()"
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <span
                                >If available, enter the absolute value of the
                                corrected ametropia, in diopters</span
                              >
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="10" md="6">
                      <v-switch
                        label="Radial keratotomy"
                        hide-details
                        v-model="inputs.is_rk"
                        @input="$v.inputs.is_rk.$touch()"
                        @blur="$v.inputs.is_rk.$touch()"
                      />
                    </v-col>
                    <v-col cols="10" md="6">
                      <v-switch
                        label="Non-physiological cornea"
                        hide-details
                        v-model="inputs.is_non_physio_cornea"
                        prepend-icon="info"
                      >
                        <v-tooltip bottom slot="prepend">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon v-on="on" v-bind="attrs" color="primary">
                              info
                            </v-icon>
                          </template>
                          <span
                            >If the anterior corneal radius is
                            non-physiological, activate this option. It will
                            remove the ARC from the IOL position predictors. NB:
                            Activation is useless for post-LVC / post-RK
                            eyes</span
                          >
                        </v-tooltip>
                      </v-switch>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4" md="3">
                      <v-switch
                        label="ICL"
                        hide-details
                        v-model="inputs.is_icl"
                        prepend-icon="info"
                      >
                        <v-tooltip bottom slot="prepend">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon v-on="on" v-bind="attrs" color="primary">
                              info
                            </v-icon>
                          </template>
                          <span
                            >If an Implantable Collamer Lens was implanted at
                            the time of biometry, activate this option. It will
                            adapt the AL measurement</span
                          >
                        </v-tooltip>
                      </v-switch>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12">
                <v-sheet class="iol-opt-v-sheet">
                  <h3>Optional : total corneal power measurement</h3>
                  <br />
                  <v-row class="justify-space-between">
                    <v-col cols="12" md="4">
                      <v-tooltip bottom slot="append">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            label="Measured Posterior Corneal Radius (mm)"
                            outlined
                            class="iol-v-text-field"
                            v-model="inputs.prc_topo"
                            :error-messages="
                              getErrors('prc_topo', $v.inputs.prc_topo)
                            "
                            type="number"
                            step="0.01"
                            @input="clearTKTCRP()"
                            @blur="clearTKTCRP()"
                            @change="clearTKTCRP()"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span
                          >Posterior corneal radius measured by corneal
                          topography. DO NOT use IOLMaster700 PRC values here
                          !</span
                        >
                      </v-tooltip>
                    </v-col>
                    <span v-if="$vuetify.breakpoint.smAndUp">OR</span>
                    <v-col cols="12" md="4">
                      <v-tooltip bottom slot="append">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            label="Total Keratometry - IOLMaster700 (D)"
                            outlined
                            class="iol-v-text-field"
                            v-model="inputs.tk"
                            :error-messages="getErrors('tk', $v.inputs.tk)"
                            type="number"
                            step="0.01"
                            @input="clearPrcTopoTCRP()"
                            @blur="clearPrcTopoTCRP()"
                            @change="clearPrcTopoTCRP()"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span>IOLMaster700 Total Keratometry Value</span>
                      </v-tooltip>
                    </v-col>
                    <span v-if="$vuetify.breakpoint.smAndUp">OR</span>
                    <v-col cols="12" md="2">
                      <v-tooltip bottom slot="append">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            label="TCRP (D)"
                            outlined
                            class="iol-v-text-field"
                            v-model="inputs.tcrp"
                            :error-messages="getErrors('tcrp', $v.inputs.tcrp)"
                            type="number"
                            step="0.01"
                            @input="clearTKPrcTopo()"
                            @blur="clearTKPrcTopo()"
                            @change="clearTKPrcTopo()"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span
                          >Total Corneal Refractive Power measured by Pentacam,
                          or other topography device using ray-tracing</span
                        >
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>
          </v-sheet>
          <v-row>
            <v-col cols="12">
              <v-divider />
              <br />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                @click="submit"
                color="#3B4256"
                class="white--text"
                x-large
              >
                Calculate
              </v-btn>
            </v-col>
          </v-row>
          <br />
          <v-row>
            <v-col cols="12" class="text-center">
              <a @click="clear" :style="$parent.resetstyle">Reset</a>
            </v-col>
          </v-row>
        </v-container>
      </form>
      <v-snackbar v-model="errorSnackbar.show" :color="errorSnackbar.color" top>
        {{ errorSnackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="errorSnackbar.show = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<style>
.v-application .primary--text {
  color: #3b4256 !important;
  caret-color: #3b4256 !important;
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import {
  required,
  requiredIf,
  minValue,
  between,
} from 'vuelidate/lib/validators'
import { solve } from '@/graphql/queries/solve.gql'
import Logout from '@/vue/Logout.vue'
import { updateAConstExt } from '@/core/sync_methods.mjs'

let omitNull = (obj) => {
  let copy = { ...obj }
  Object.keys(copy)
    .filter((k) => copy[k] === null)
    .forEach((k) => delete copy[k])
  return copy
}

export default {
  mixins: [validationMixin],
  components: {
    Logout,
  },
  validations() {
    return {
      inputs: {
        al: { required, between: between(17, 40) },
        acd: { required, between: between(1, 6) },
        k1: { required, between: between(30, 60) },
        k2: {
          required,
          between: between(30, 60),
          minValue: minValue(this.inputs.k1),
        },
        lt: { between: between(2.5, 8) },
        cct: {
          required: requiredIf(function(form) {
            return this.inputs.tk
          }),
          between: between(350, 700),
        },
        target: { required, between: between(-4, 1.5) },
        aconst: {
          required: requiredIf(function(form) {
            return this.inputs.iol_model.type != '60MA'
          }),
          between: between(117, 121),
        },
        pre_lvc_arc: { between: between(5.5, 9.5) },
        laser_amount: { between: between(0.5, 12) },
        prc_topo: { between: between(3, 999) },
        tk: { between: between(25, 75) },
        tcrp: { between: between(25, 75) },
      },
    }
  },
  mounted() {
    if (this.$route.query.ocr) {
      this.$v.inputs.$touch()
    }
    // Initialize side from currentSide (from last calculation)
    this.inputs.side = this.currentSide
    
    // Load inputs based on currentSide
    if (this.currentSide) {
      this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs) }
    } else {
      this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs2) }
    }
    
    // Clean up properties from Second that don't exist in Complex
    delete this.inputs.is_second
    delete this.inputs.al2
    delete this.inputs.k12
    delete this.inputs.k22
    delete this.inputs.acd2
    delete this.inputs.lt2
    delete this.inputs.cct2
    delete this.inputs.power2
    delete this.inputs.se2
    delete this.inputs.laser_amount2
    delete this.inputs.pre_lvc_arc2
  },
  data: () => {
    let ret = {
      inputs: {
        patientID: '',
        side: true,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        is_corneal_laser: false,
        is_icl: false,
        corneal_laser_type: { type: 'Myopic', text: 'Myopic' },
        is_non_physio_cornea: false,
        is_rk: false,
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        pre_lvc_arc: '',
        laser_amount: '',
        target: 0,
        prc_topo: '',
        tk: '',
        tcrp: '',
      },
      inputs_default: {
        patientID: '',
        side: true,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        is_icl: false,
        is_corneal_laser: false,
        corneal_laser_type: { type: 'Myopic', text: 'Myopic' },
        is_non_physio_cornea: false,
        is_rk: false,
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        pre_lvc_arc: '',
        laser_amount: '',
        target: 0,
        prc_topo: '',
        tk: '',
        tcrp: '',
      },
      errorSnackbar: {
        show: false,
        text: '',
        color: 'error',
      },
    }
    if (process.env.NODE_ENV == 'development') {
      ret.inputs = {
        ...ret.inputs,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        al: 23,
        acd: 4,
        k1: 33,
        k2: 33,
        lt: 5,
        cct: 500,
        target: 1,
        is_icl: true,
        is_non_physio_cornea: true,
        is_rk: true,
        is_corneal_laser: true,
        corneal_laser_type: { type: 'Myopic', text: 'Myopic' },
        pre_lvc_arc: 7,
        laser_amount: 9,
        prc_topo: 123,
        patientID: 'testid',
      }
    }
    return ret
  },

  computed: {
    ...mapState(['appHeader', 'ginputs', 'ginputs2', 'currentSide']),
  },

  watch: {
    'inputs.side': function(newValue) {
      // OD = true, OS = false
      if (newValue) {
        // Switch to OD - load ginputs
        this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs) }
      } else {
        // Switch to OS - load ginputs2
        this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs2) }
      }
      
      // Update currentSide in store
      this.$store.dispatch('updateCurrentSide', newValue)
      
      // Clean up properties from Second that don't exist in Complex
      delete this.inputs.is_second
      delete this.inputs.al2
      delete this.inputs.k12
      delete this.inputs.k22
      delete this.inputs.acd2
      delete this.inputs.lt2
      delete this.inputs.cct2
      delete this.inputs.power2
      delete this.inputs.se2
      delete this.inputs.laser_amount2
      delete this.inputs.pre_lvc_arc2
    },
    inputs: {
      handler: function(newValue) {
        // Auto-save to the correct store when inputs change
        if (newValue.side) {
          this.$store.dispatch('updateInputs', newValue)
        } else {
          this.$store.dispatch('updateInputs2', newValue)
        }
      },
      deep: true,
    },
  },

  methods: {
    getErrors(name, model) {
      const errors = []
      if (!model.$dirty) return errors
      switch (name) {
        case 'k2':
          !model.minValue && errors.push('K2 must be greater than K1')
        case 'al':
        case 'acd':
        case 'k1':
        case 'k2':
        case 'target':
        case 'aconst':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'cct':
          this.$v.inputs.tk && !model.required && errors.push('required')
        case 'lt':
        case 'cct':
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        case 'pre_lvc_arc':
        case 'laser_amount':
          this.inputs.is_corneal_laser &&
            model.required &&
            errors.push('required')
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        case 'prc_topo':
        case 'tk':
        case 'tcrp':
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        default:
          break
      }
      return errors
    },
    clearTKTCRP() {
      if (this.inputs.prc_topo !== '') {
        this.inputs.tk = ''
        this.inputs.tcrp = ''
      }
    },
    clearPrcTopoTCRP() {
      if (this.inputs.tk !== '') {
        this.inputs.prc_topo = ''
        this.inputs.tcrp = ''
      }
    },
    clearTKPrcTopo() {
      if (this.inputs.tcrp !== '') {
        this.inputs.tk = ''
        this.inputs.prc_topo = ''
      }
    },
    updateAConst(input) {
      updateAConstExt(this.$parent, input)
    },
    focusFirstError(component = this) {
      if (component.hasError) {
        component.focus()
        return true
      }

      let focused = false

      component.$children.some((childComponent) => {
        focused = this.focusFirstError(childComponent)
        return focused
      })

      return focused
    },
    submit() {
      this.$v.inputs.$touch()
      this.$nextTick(() => {
        if (this.focusFirstError()) {
          return
        }
        Object.keys(this.inputs).forEach(
          (k) =>
            (this.inputs[k] = this.inputs[k] === '' ? null : this.inputs[k]),
        )
        const inputs = { ...omitNull(this.inputs) }
        // Store inputs based on which eye is selected
        if (this.inputs.side) {
          // OD - save to ginputs
          this.$store.dispatch('updateInputs', this.inputs)
        } else {
          // OS - save to ginputs2
          this.$store.dispatch('updateInputs2', this.inputs)
        }

        // send type to get_result
        inputs['iol_model'] = inputs['iol_model'].type
        inputs['biometer'] = inputs['biometer'].type
        inputs['corneal_laser_type'] = inputs['corneal_laser_type'].type

        // do not send corneal history if is_corneal false
        if (inputs['is_corneal_laser'] === false) {
          delete inputs['corneal_laser_type']
          delete inputs['laser_amount']
          delete inputs['pre_lvc_arc']
        }

        // delete those inputs because not used in calculation, so not sent via graphql,
        // only stored in ginputs for reuse on other tab
        delete inputs['patientID']
        
        // Clean up properties from Second that should not be sent in Complex mode
        delete inputs['is_second']
        delete inputs['al2']
        delete inputs['k12']
        delete inputs['k22']
        delete inputs['acd2']
        delete inputs['lt2']
        delete inputs['cct2']
        delete inputs['power2']
        delete inputs['se2']
        delete inputs['laser_amount2']
        delete inputs['pre_lvc_arc2']

        this.$apollo
          .query({
            query: solve,
            variables: {
              inputs,
            },
          })
          .then((result) => {
            // Save to the correct store based on which eye is selected
            if (this.inputs.side) {
              // OD - save to results
              this.$store.dispatch('setResults', result.data.solve)
            } else {
              // OS - save to results2
              this.$store.dispatch('setResults2', result.data.solve)
            }
            // Store which eye was calculated
            this.$store.dispatch('updateCurrentSide', this.inputs.side)
            this.$store.dispatch('updateLastCalc', 'complex')
            this.$router.push('result_complex')
          })
          .catch((e) => {
            this.$raven.captureException(e)
            this.errorSnackbar.text =
              e.message || 'An error occurred during calculation'
            this.errorSnackbar.show = true
          })
      })
    },
    clear() {
      Object.keys(this.inputs).forEach(
        (k) =>
          (this.inputs[k] =
            this.inputs_default[k] !== null ? this.inputs_default[k] : null),
      )
      this.$store.dispatch('resetInputs')
      this.$store.dispatch('resetInputs2')
      this.$refs.first.focus()
    },
    goToCamera() {
      this.$router.push({ name: 'CameraCapture' })
    },
    ...mapActions(['updateInputs', 'updateInputs2', 'setResults', 'setAuthUser', 'resetInputs', 'resetInputs2', 'updateCurrentSide']),
  },
}
</script>
